import React, { useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Close } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

export default function GlobalSearch({ isSearch, setIsSearch }) {
  const [text, setText] = useState("");
  const [searchData, setSearchData] = useState([]);
  const abortControllerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const Navigate = useNavigate();

  const handleClose = () => {
    setIsSearch(false);
  };

  useEffect(() => {
    const trimmedText = text.trim(); // Trim the input

    if (!trimmedText) return; // Prevent API call for empty or spaces

    // Cancel the previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new abort controller
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const searchFunc = async () => {
      setLoading(true);
      try {
        let payload = {
          SearchTerm: text,
        };
        const response = await global.$axios.post(
          "ticket/OverAllsearchTicketList",
          payload,
          {
            signal: abortController.signal,
          }
        );
        // console.log("OverAllsearchTicketList", response.data);

        if (response.data.Status === 200) {
          setSearchData(response.data.Message || []);
        } else {
          setSearchData([]);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Search request failed:", error);
        }
      } finally {
        setLoading(false);
      }
    };

    // Debounce API Call
    const debounceTimer = setTimeout(() => {
      searchFunc();
    }, 500); // Adjust debounce delay as needed (e.g., 500ms)

    return () => {
      clearTimeout(debounceTimer);
      abortController.abort(); // Cleanup previous API call
    };
  }, [text]);

  const onClose = () => {
    setText("");
    setSearchData([]);
  };

  return (
    <React.Fragment>
      <Dialog
        open={isSearch}
        onClose={handleClose}
        scroll={"paper"}
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
        fullWidth={true}
        maxWidth={"sm"}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 2,
            maxHeight: 400,
          },
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0px 12px",
            paddingTop: "2px",
            paddingRight: "14px",
            gap: 10,
          }}
        >
          <Box
            id="searcBox"
            sx={{
              flexDirection: "row",
              display: "flex",
              alignItems: "center",
              padding: "3px",
              background: "#fff",
              textAlign: "center",
              width: "95%",
              alignSelf: "center",
              margin: "10px 0px",
              gap: "10px",
            }}
          >
            <SearchIcon style={{ color: "#0071BC" }} />
            <input
              placeholder="Search tickets.."
              type="text"
              className="searcInput"
              id="searchInput"
              style={{
                outline: "none",
                backgroundColor: "#FFFFFF",
                border: "none",
                width: "100%",
              }}
              value={text}
              onChange={(e) => setText(e.target.value)}
              autoComplete="off"
              autoFocus={true}
            />
            {text && (
              <div style={{ cursor: "pointer" }} onClick={onClose}>
                <Close style={{ color: "#0071BC" }} />
              </div>
            )}
          </Box>
          <div onClick={handleClose}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                paddingX: 1,
                paddingY: 0.5,
                backgroundColor: "#f5f5f5",
                borderRadius: "6px",
                border: "1px solid #e0e0e0",
                fontSize: "12px",
                color: "#0071BC",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              <div style={{ fontSize: "11px" }}>Esc</div>
            </Box>
          </div>
        </div>

        <DialogContent sx={{ maxHeight: 400 }} dividers={"paper"}>
          <DialogContentText id="scroll-dialog-description">
            {loading ? (
              <Grid
                container
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 150,
                }}
              >
                <CircularProgress size="3rem" />
              </Grid>
            ) : (
              <Grid container>
                {searchData.length > 0 ? (
                  <>
                    {searchData.map((item, index) => {
                      const issue = item.issue;
                      const product = item?.product;
                      const department = item.dept;
                      const assigned = item.assigned;
                      return (
                        <Grid
                          container
                          xs={12}
                          item
                          key={item.TicketId}
                          sx={{
                            marginBottom: "12px",
                            border: "1px solid #E0E7ED",
                            padding: "16px",
                            borderRadius: "12px",
                            cursor: "pointer",
                            backgroundColor: "#FFFFFF",
                            transition: "all 0.2s ease",
                            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
                            "&:hover": {
                              backgroundColor: "#F8FBFD",
                              borderColor: "#2196F3",
                              boxShadow: "0 4px 12px rgba(33, 150, 243, 0.15)",
                              transform: "translateY(-2px)",
                            },
                          }}
                          onClick={() => {
                            Navigate("searchView", {
                              state: { ticketId: item.TicketId },
                            });
                            setIsSearch(false);
                          }}
                        >
                          {/* Header with Ticket ID and Status */}
                          <Grid
                            item
                            xs={12}
                            sx={{
                              marginBottom: "12px",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "16px",
                                fontWeight: 600,
                                color: "#1A1A1A",
                              }}
                            >
                              #{item.TicketId}
                            </Typography>
                            <Box
                              sx={{
                                padding: "4px 12px",
                                borderRadius: "16px",
                                backgroundColor:
                                  item.Status === "New"
                                    ? "#E3F2FD"
                                    : item.Status === "Progress"
                                    ? "#FFF9E6"
                                    : item.Status === "Hold"
                                    ? "#FFF4E6"
                                    : item.Status === "Solved"
                                    ? "#F0FDF4"
                                    : item.Status === "Completed"
                                    ? "#E6F7F4"
                                    : "#F5F5F5",
                                color:
                                  item.Status === "New"
                                    ? "#1976D2"
                                    : item.Status === "Progress"
                                    ? "#E3B719"
                                    : item.Status === "Hold"
                                    ? "#F99C35"
                                    : item.Status === "Completed"
                                    ? "#0A9B7C"
                                    : item.Status === "Solved"
                                    ? "#84E330"
                                    : "#757575",
                                fontSize: "13px",
                                fontWeight: 500,
                              }}
                            >
                              {item.Status}
                            </Box>
                          </Grid>

                          {/* Main Content */}
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ marginBottom: "8px" }}>
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    color: "#6B7280",
                                    marginBottom: "2px",
                                  }}
                                >
                                  Issue
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    color: "#1F2937",
                                  }}
                                >
                                  {issue?.IssueName || "N/A"}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    color: "#6B7280",
                                    marginBottom: "2px",
                                  }}
                                >
                                  Product
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    color: "#1F2937",
                                  }}
                                >
                                  {product?.ProductName || "N/A"}
                                </Typography>
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Box sx={{ marginBottom: "8px" }}>
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    color: "#6B7280",
                                    marginBottom: "2px",
                                  }}
                                >
                                  Assigned To
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    color: "#1F2937",
                                  }}
                                >
                                  {assigned?.UserFirstName || "Unassigned"}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    color: "#6B7280",
                                    marginBottom: "2px",
                                  }}
                                >
                                  Department
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    color: "#1F2937",
                                  }}
                                >
                                  {department?.DepartmentName || "N/A"}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                      );
                    })}{" "}
                  </>
                ) : (
                  <Grid
                    container
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="h6" sx={{ color: "#232323" }}>
                      No Tickets Found
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
}

const styles = {
  txt: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#23232380",
  },
  txt1: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#232323",
  },
};
